import { useEffect, useState, useRef } from "react";
import { Maximize2, Minimize2 } from "lucide-react";

/**
 * CINEMA MODE — per-visitor toggle. When on, all UI chrome fades out; content + music remain.
 * The pill itself auto-hides after ~5 s of no mouse/keyboard activity while cinema is on,
 * and reappears the moment the visitor moves the mouse or touches the screen.
 * Exit: click again or press Escape.
 * Entrance: brief letterbox-curtain animation (Netflix "curtain up" feel).
 */
export default function CinemaToggle() {
  const [on, setOn] = useState(false);
  const [idle, setIdle] = useState(false);
  const [entering, setEntering] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const idleTimer = useRef(null);

  useEffect(() => {
    try {
      setOn(localStorage.getItem("bes_cinema") === "1");
      // First-visit hint — pops after 5 s if the user hasn't seen it before
      if (!localStorage.getItem("bes_cinema_hinted")) {
        setTimeout(() => setShowHint(true), 5000);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try { localStorage.setItem("bes_cinema", on ? "1" : "0"); } catch { /* ignore */ }
    document.documentElement.dataset.cinema = on ? "true" : "false";
    document.documentElement.dataset.cinemaIdle = "false";
    setIdle(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
  }, [on]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && on) setOn(false); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [on]);

  useEffect(() => {
    if (!on) { setIdle(false); return; }
    function wake() {
      setIdle(false);
      document.documentElement.dataset.cinemaIdle = "false";
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        setIdle(true);
        document.documentElement.dataset.cinemaIdle = "true";
      }, 5000);
    }
    wake();
    const evs = ["mousemove", "keydown", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, wake, { passive: true }));
    return () => {
      evs.forEach((e) => window.removeEventListener(e, wake));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [on]);

  function enter() {
    try { localStorage.setItem("bes_cinema_hinted", "1"); } catch { /* ignore */ }
    setShowHint(false);
    if (!on) {
      setEntering(true);
      setTimeout(() => setEntering(false), 1400);
    }
    setOn((v) => !v);
  }

  return (
    <>
      {entering && <div className="cinema-entrance" aria-hidden />}
      {showHint && !on && (
        <div
          className="fixed bottom-24 right-6 z-[54] max-w-[240px] p-4 pointer-events-none"
          style={{
            borderRadius: "var(--r-lg)",
            border: "1px solid var(--line-gold)",
            background: "rgba(11,11,13,0.92)",
            backdropFilter: "blur(12px)",
            animation: "hintFade 700ms ease forwards",
          }}
          data-testid="cinema-hint"
        >
          <div className="font-mono text-[9px] uppercase tracking-[0.4em]" style={{ color: "var(--gold)" }}>Try It</div>
          <div className="mt-2 font-body text-sm" style={{ color: "var(--text-2)", lineHeight: 1.5 }}>
            Tap <span style={{ color: "var(--gold)" }}>Cinema</span> to hide all UI and let the imagery + music take over.
          </div>
        </div>
      )}
      <button
        onClick={enter}
        data-testid="cinema-toggle"
        data-cursor="hover"
        data-cursor-label={on ? "Exit" : "Cinema"}
        aria-label={on ? "Exit cinema mode" : "Enter cinema mode"}
        className="fixed bottom-6 right-6 z-[55] h-12 flex items-center gap-3 px-4 transition-all duration-700 group"
        style={{
          borderRadius: "var(--r-full)",
          border: `1px solid ${on ? "var(--gold)" : "var(--line-gold)"}`,
          background: on ? "rgba(198,168,124,0.14)" : "rgba(11,11,13,0.72)",
          color: on ? "var(--gold)" : "var(--text-2)",
          backdropFilter: "blur(12px)",
          boxShadow: showHint ? "0 0 32px rgba(198,168,124,0.6)" : (on ? "0 0 24px rgba(198,168,124,0.22)" : "0 4px 18px rgba(0,0,0,0.4)"),
          opacity: idle ? 0 : 1,
          transform: idle ? "translateY(24px)" : "translateY(0)",
          pointerEvents: idle ? "none" : "auto",
          animation: showHint ? "cinemaPulse 2s ease-in-out infinite" : "none",
        }}
      >
        {on ? <Minimize2 size={15} strokeWidth={1.5} /> : <Maximize2 size={15} strokeWidth={1.5} />}
        <span className="font-mono text-[9px] uppercase tracking-[0.36em] whitespace-nowrap">
          {on ? "Exit Cinema · Esc" : "Cinema"}
        </span>
      </button>
    </>
  );
}
